import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authClient, useListOrganizations } from '@/lib/auth-client';

interface InviteUserFormProps {
  onSuccess?: () => void;
  preselectedTenantId?: string;
}

// Map our app roles to Better Auth organization roles
type AppRole = 'user' | 'admin';
type BetterAuthRole = 'member' | 'admin' | 'owner';

const roleToBetterAuth: Record<AppRole, BetterAuthRole> = {
  user: 'member',
  admin: 'admin',
};

// Type for Better Auth organization inviteMember method
interface InviteMemberParams {
  email: string;
  role: BetterAuthRole;
  organizationId: string;
}

interface InviteMemberResult {
  error?: { message?: string };
  data?: { id: string };
}

export function InviteUserForm({ onSuccess, preselectedTenantId }: InviteUserFormProps) {
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState(preselectedTenantId || '');
  const [role, setRole] = useState<AppRole>('user');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Use Better Auth's organization list - this only returns orgs the user is a member of
  // Super admins are members of all orgs, so they see all tenants
  const { data: organizations } = useListOrganizations();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      // Cast to access organization methods
      const inviteMember = (
        authClient as unknown as {
          organization: {
            inviteMember: (params: InviteMemberParams) => Promise<InviteMemberResult>;
          };
        }
      ).organization.inviteMember;

      const result = await inviteMember({
        email,
        role: roleToBetterAuth[role],
        organizationId: tenantId,
      });

      if (result.error) {
        throw new Error(result.error.message || 'Failed to send invite');
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setEmail('');
      setRole('user');
      setError(null);
      setSuccess('Invitation sent successfully');
      onSuccess?.();
    },
    onError: (err: Error) => {
      setError(err.message);
      setSuccess(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!tenantId) {
      setError('Please select a tenant');
      return;
    }

    mutation.mutate();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      {success && <Alert severity="success">{success}</Alert>}

      <TextField
        type="email"
        label="Email Address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="user@example.com"
        required
        fullWidth
      />

      {!preselectedTenantId && (
        <FormControl fullWidth required>
          <InputLabel>Organization</InputLabel>
          <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)} label="Organization">
            <MenuItem value="" disabled>
              Select an organization
            </MenuItem>
            {organizations?.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl fullWidth>
        <InputLabel>Role</InputLabel>
        <Select value={role} onChange={(e) => setRole(e.target.value as AppRole)} label="Role">
          <MenuItem value="user">User (Member)</MenuItem>
          <MenuItem value="admin">Tenant Admin</MenuItem>
        </Select>
      </FormControl>

      <Button type="submit" variant="contained" fullWidth disabled={mutation.isPending}>
        {mutation.isPending ? 'Sending Invitation...' : 'Send Invitation'}
      </Button>
    </Box>
  );
}
